import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  BadgeCheck,
  MapPin,
  Briefcase,
  Globe,
  Heart,
  ArrowLeft,
  MessageCircle,
  Flag,
  Edit,
  User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);

  const [editForm, setEditForm] = useState({
    profession: "",
    location: "",
    bio: "",
    expertise: "",
    hobbies: "",
    travel_bucket_list: "",
  });

  const [reportForm, setReportForm] = useState({
    reason: "",
    details: "",
  });

  const isOwnProfile = currentUser?.user_id === userId;

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      if (isOwnProfile) {
        setProfile(currentUser);
        setEditForm({
          profession: currentUser?.profession || "",
          location: currentUser?.location || "",
          bio: currentUser?.bio || "",
          expertise: currentUser?.expertise?.join(", ") || "",
          hobbies: currentUser?.hobbies?.join(", ") || "",
          travel_bucket_list: currentUser?.travel_bucket_list?.join(", ") || "",
        });
      } else {
        const response = await axios.get(`${API}/profile/${userId}`, {
          withCredentials: true,
        });
        setProfile(response.data);
      }
    } catch (error) {
      toast.error("Failed to load profile");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const payload = {
        profession: editForm.profession,
        location: editForm.location,
        bio: editForm.bio,
        expertise: editForm.expertise.split(",").map((s) => s.trim()).filter(Boolean),
        hobbies: editForm.hobbies.split(",").map((s) => s.trim()).filter(Boolean),
        travel_bucket_list: editForm.travel_bucket_list.split(",").map((s) => s.trim()).filter(Boolean),
      };

      await axios.put(`${API}/profile`, payload, { withCredentials: true });
      await refreshUser();
      setIsEditing(false);
      toast.success("Profile updated!");
      fetchProfile();
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleReport = async () => {
    try {
      await axios.post(
        `${API}/reports`,
        {
          reported_user_id: userId,
          reason: reportForm.reason,
          details: reportForm.details,
        },
        { withCredentials: true }
      );
      toast.success("Report submitted. We'll review it shortly.");
      setShowReportDialog(false);
      setReportForm({ reason: "", details: "" });
    } catch (error) {
      toast.error("Failed to submit report");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <p className="text-caption">Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <p className="text-caption">Profile not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory">
      {/* Header */}
      <header className="border-b border-border bg-ivory/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container-luxury flex items-center justify-between h-20">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-charcoal hover:text-gold transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-caption">Back to Dashboard</span>
          </button>

          {!isOwnProfile && (
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate(`/messages/${userId}`)}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="message-btn"
              >
                <MessageCircle className="w-4 h-4" />
                Message
              </Button>

              <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    className="text-destructive hover:text-destructive/80"
                    data-testid="report-btn"
                  >
                    <Flag className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-ivory border-border">
                  <DialogHeader>
                    <DialogTitle className="text-h3 text-charcoal">
                      Report for Romantic Advances
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    <p className="text-body text-muted-foreground">
                      Havns is strictly platonic. Report any romantic
                      or dating-related behavior immediately.
                    </p>
                    <div className="form-group">
                      <label className="form-label">Reason *</label>
                      <Input
                        value={reportForm.reason}
                        onChange={(e) =>
                          setReportForm({ ...reportForm, reason: e.target.value })
                        }
                        placeholder="e.g., Romantic messages, Dating requests"
                        className="input-luxury"
                        data-testid="report-reason-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Details *</label>
                      <Textarea
                        value={reportForm.details}
                        onChange={(e) =>
                          setReportForm({ ...reportForm, details: e.target.value })
                        }
                        placeholder="Please describe the incident..."
                        className="input-luxury min-h-[120px] resize-none"
                        data-testid="report-details-input"
                      />
                    </div>
                    <Button
                      onClick={handleReport}
                      disabled={!reportForm.reason || !reportForm.details}
                      className="btn-primary w-full"
                      data-testid="submit-report-btn"
                    >
                      Submit Report
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {isOwnProfile && !isEditing && (
            <Button
              onClick={() => setIsEditing(true)}
              variant="outline"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="edit-profile-btn"
            >
              <Edit className="w-4 h-4" />
              Edit Profile
            </Button>
          )}
        </div>
      </header>

      {/* Profile Content */}
      <main className="container-luxury py-12">
        <div className="max-w-3xl mx-auto">
          {/* Profile Header */}
          <div className="flex items-start gap-8 mb-12">
            {profile.picture ? (
              <img
                src={profile.picture}
                alt={profile.name}
                className="w-32 h-32 object-cover"
              />
            ) : (
              <div className="w-32 h-32 bg-secondary flex items-center justify-center">
                <User className="w-16 h-16 text-muted-foreground" />
              </div>
            )}

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-h1 text-charcoal">{profile.name}</h1>
                {profile.is_verified && (
                  <div className="badge-verified" data-testid="profile-verified-badge">
                    <BadgeCheck className="w-5 h-5" />
                    <span>Verified Professional</span>
                  </div>
                )}
              </div>

              {profile.profession && (
                <div className="flex items-center gap-2 text-body-lg mb-2">
                  <Briefcase className="w-5 h-5 text-gold" />
                  <span>{profile.profession}</span>
                </div>
              )}

              {profile.location && (
                <div className="flex items-center gap-2 text-body text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span>{profile.location}</span>
                </div>
              )}
            </div>
          </div>

          {/* Edit Mode */}
          {isEditing && (
            <div className="bg-white border border-border p-8 mb-12 animate-fade-in">
              <h2 className="text-h3 text-charcoal mb-6">Edit Profile</h2>
              <div className="space-y-6">
                <div className="form-group">
                  <label className="form-label">Profession</label>
                  <Input
                    value={editForm.profession}
                    onChange={(e) =>
                      setEditForm({ ...editForm, profession: e.target.value })
                    }
                    className="input-luxury"
                    data-testid="edit-profession-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Location</label>
                  <Input
                    value={editForm.location}
                    onChange={(e) =>
                      setEditForm({ ...editForm, location: e.target.value })
                    }
                    className="input-luxury"
                    data-testid="edit-location-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Bio</label>
                  <Textarea
                    value={editForm.bio}
                    onChange={(e) =>
                      setEditForm({ ...editForm, bio: e.target.value })
                    }
                    className="input-luxury min-h-[100px] resize-none"
                    data-testid="edit-bio-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Expertise (comma-separated)</label>
                  <Input
                    value={editForm.expertise}
                    onChange={(e) =>
                      setEditForm({ ...editForm, expertise: e.target.value })
                    }
                    className="input-luxury"
                    data-testid="edit-expertise-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Hobbies (comma-separated)</label>
                  <Input
                    value={editForm.hobbies}
                    onChange={(e) =>
                      setEditForm({ ...editForm, hobbies: e.target.value })
                    }
                    className="input-luxury"
                    data-testid="edit-hobbies-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Travel Bucket List (comma-separated)</label>
                  <Input
                    value={editForm.travel_bucket_list}
                    onChange={(e) =>
                      setEditForm({ ...editForm, travel_bucket_list: e.target.value })
                    }
                    className="input-luxury"
                    data-testid="edit-bucket-list-input"
                  />
                </div>
                <div className="flex gap-4">
                  <Button
                    onClick={handleSaveProfile}
                    className="btn-primary"
                    data-testid="save-profile-btn"
                  >
                    Save Changes
                  </Button>
                  <Button
                    onClick={() => setIsEditing(false)}
                    variant="outline"
                    className="btn-secondary"
                    data-testid="cancel-edit-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Bio */}
          {profile.bio && (
            <div className="mb-12">
              <h2 className="text-caption mb-4">About</h2>
              <p className="text-body-lg">{profile.bio}</p>
            </div>
          )}

          <div className="divider" />

          {/* Expertise */}
          {profile.expertise?.length > 0 && (
            <div className="mb-12">
              <h2 className="text-caption mb-4 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-gold" />
                Areas of Expertise
              </h2>
              <div className="flex flex-wrap gap-3">
                {profile.expertise.map((item, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-white border border-border text-sm font-mono uppercase tracking-wider"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Hobbies */}
          {profile.hobbies?.length > 0 && (
            <div className="mb-12">
              <h2 className="text-caption mb-4 flex items-center gap-2">
                <Heart className="w-4 h-4 text-gold" />
                Hobbies & Interests
              </h2>
              <div className="flex flex-wrap gap-3">
                {profile.hobbies.map((item, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-white border border-border text-sm font-mono uppercase tracking-wider"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Travel Bucket List */}
          {profile.travel_bucket_list?.length > 0 && (
            <div className="mb-12">
              <h2 className="text-caption mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4 text-gold" />
                Travel Bucket List
              </h2>
              <div className="flex flex-wrap gap-3">
                {profile.travel_bucket_list.map((item, idx) => (
                  <span
                    key={idx}
                    className="px-4 py-2 bg-gold/10 text-gold text-sm font-mono uppercase tracking-wider"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Profile;
